import CampaignIcon from "@mui/icons-material/Campaign";
import Diversity3Icon from "@mui/icons-material/Diversity3";
import DriveEtaIcon from "@mui/icons-material/DriveEta";
import ExitToAppIcon from "@mui/icons-material/ExitToApp";
import { ErrorBoundary } from "@sentry/react";
import Cookies from "js-cookie";
import React, { useCallback, useContext, useState } from "react";
import { useHistory } from "react-router-dom";
import { toast, ToastContainer } from "react-toastify";
import "../../../assets/styles/pages/igniteMS.scss";
import { ErrorMsg } from "../../../components/AppMessages";
import { DashboardLayout } from "../../../components/Layouts";
import { BLACK, DARK_ACCENT, WHITE } from "../../../constants";
import { ThemeContext } from "../../../context";
import { useRequestAccessByPath } from "../../../hooks/react-query/access-requests/useRequestAccessByPath";
import { eventTracking, MixpanelEvents } from "../../../utils/userTracking";

interface DashboardItem {
  id: string;
  name: string;
  title: string[];
  icon: any;
  route: string;
  isRestricted: boolean;
}

export const IgniteMs = () => {
  const history = useHistory();
  const { theme } = useContext(ThemeContext);

  const [activeBoard, setActiveBoard] = useState<string>("");

  const viewsCookie = Cookies.get("views");
  let userViews: string[] = [];

  if (viewsCookie) {
    try {
      // The cookie stores a JSON array of arrays
      userViews = JSON.parse(viewsCookie) || [];
    } catch (err) {
      console.error("Failed to parse views cookie:", err);
    }
  }

  const dashboards: DashboardItem[] = [
    {
      id: "reignite_user",
      name: "Sales & Inventory",
      title: ["Sales &", " Inventory"],
      icon: DriveEtaIcon,
      route: "/global_ms_sales/tier_one",
      isRestricted: !userViews.includes("reignite_user"), // Set to true to show the restricted banner
    },
    {
      id: "reignite_user",
      name: "Marketing & Leads Generation",
      title: ["Marketing & Leads", " Generation"],
      icon: Diversity3Icon,
      route: "/global_ms_sales/tier_two",
      isRestricted: !userViews.includes("reignite_manager"),
    },
    {
      id: "reignite_manager",
      name: "Marketing Performance",
      title: ["Marketing", " Performance"],
      icon: CampaignIcon,
      route: "/global_ms_sales/tier_three",
      isRestricted: !userViews.includes("reignite_user"),
    },
  ];

  const handleSetNissanAccount = () => {
    history.push("/faq/link_google_account?dashboard=global_ms_sales");
  };

  const handleSetNissanAccountPPT = () => {
    history.push("/global_ms_sales/handbook_pdf_page");
  };

  const { requestAccessByPath } = useRequestAccessByPath();

  const handleRequestAccess = useCallback(
    (e, dashboard: DashboardItem) => {
      e.preventDefault();
      requestAccessByPath(dashboard.route, {
        onSuccess: () => {
          eventTracking(MixpanelEvents.dashboard_access_request, { dashboard: dashboard.name });
          toast.success("Request for access has been received");
        },
        onError: () => toast.error("Failed to update your request, please try again later"),
      });
    },
    [requestAccessByPath]
  );

  return (
    <DashboardLayout>
      <ErrorBoundary fallback={<ErrorMsg class={"formError"} />}>
        <div
          className="main_dashboard_ignite"
          style={{
            backgroundColor: theme === "light" ? "#e7ecf0" : "#1a1a1a",
            color: theme === "light" ? BLACK : WHITE,
          }}
        >
          <h1 className="title">G-M&S Re:Ignite Control Tower</h1>
          <div className="section">
            <p>
              G-M&S Re:Ignite Control Tower is a global executive dashboard for critical Sales and Marketing KPIs. It is intended for a
              specific audience and access is granted exclusively to authorized users. To access it, please ensure you are logged in with
              your Nissan-associated Google account or refer to the link below for information on how to set up a Google account.{" "}
            </p>
          </div>
          <div className="main-cedar-dashboard">
            {dashboards.map((dashboard) => {
              const IconComponent = dashboard.icon;
              return (
                <div
                  key={dashboard.id}
                  className={`section ${dashboard.isRestricted ? "restricted" : ""}`}
                  style={{ backgroundColor: theme === "light" ? WHITE : "#2c2c2c" }}
                >
                  {dashboard.isRestricted && (
                    <div className="restricted_banner">
                      <p>Access to this dashboard is currently restricted.</p>
                      <a href="#" className="request_link" onClick={(e) => handleRequestAccess(e, dashboard)}>
                        Request Access
                      </a>
                      {/* <Link
                        className="access_link"
                        to="#"
                        onClick={(e) => handleRequestAccess(e, dashboard)}
                        data-test-id={`access_link_${dashboard.id}`}
                      >
                        Request access
                      </Link> */}
                    </div>
                  )}
                  <div className="dashboard_group">
                    <div className="group_title">
                      <IconComponent
                        fontSize="large"
                        className="group_icon"
                        sx={{ color: theme === "light" ? DARK_ACCENT : WHITE, width: "100%", height: "100%" }}
                      />
                    </div>
                    <div className="title_description" style={{ color: theme === "light" ? BLACK : WHITE }}>
                      {dashboard.title.map((line, idx) => (
                        <p key={idx} style={{ color: theme === "light" ? BLACK : WHITE }}>
                          {line}
                        </p>
                      ))}
                    </div>
                    <div
                      className="group_title"
                      onClick={!dashboard.isRestricted ? () => history.push(dashboard.route) : undefined}
                      style={{ cursor: dashboard.isRestricted ? "default" : "pointer" }}
                    >
                      <ExitToAppIcon
                        fontSize="large"
                        className="goto_icon"
                        sx={{ color: theme === "light" ? DARK_ACCENT : WHITE, width: "100%", height: "100%" }}
                      />
                    </div>
                  </div>
                </div>
              );
            })}
          </div>

          <div className="section">
            <button className="googleAccountBtn" onClick={handleSetNissanAccount} style={{ color: theme === "light" ? WHITE : BLACK }}>
              How to set your Nissan / Google Account
            </button>
            <button className="googleAccountBtn" onClick={handleSetNissanAccountPPT} style={{ color: theme === "light" ? WHITE : BLACK }}>
              G-M&S Re:Ignite Control Handbook
            </button>
          </div>
        </div>
        <ToastContainer
          position="top-right"
          autoClose={2500}
          hideProgressBar={true}
          newestOnTop={true}
          closeOnClick={true}
          draggable={false}
          pauseOnHover={false}
        />
      </ErrorBoundary>
    </DashboardLayout>
  );
};
